import type { ReactNode } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Table } from '@shared/components';
import type { QuoteLineView } from '../../utils/quoteSummaryLines';

interface DateColumn<T> {
    header: string;
    value: (line: T) => string;
}

interface Props<T extends QuoteLineView> {
    title?: string;
    lines: T[];
    emptyMessage: string;
    dateColumn?: DateColumn<T>;
    renderNameCell?: (line: T) => ReactNode;
}

const getLineId = (line: QuoteLineView): string => line.id;

export function QuoteLinesTable<T extends QuoteLineView>({
    title,
    lines,
    emptyMessage,
    dateColumn,
    renderNameCell,
}: Props<T>) {
    const dateColumns: ColumnDef<T>[] = dateColumn
        ? [{ id: 'date', header: dateColumn.header, cell: ({ row }) => dateColumn.value(row.original) }]
        : [];

    const columns: ColumnDef<T>[] = [
        {
            id: 'name',
            header: 'Name',
            cell: ({ row }) => (renderNameCell ? renderNameCell(row.original) : row.original.name),
        },
        { id: 'description', header: 'Description', cell: ({ row }) => row.original.description },
        ...dateColumns,
        {
            id: 'net',
            header: 'Net',
            cell: ({ row }) => <span data-testid="line-net">{row.original.net}</span>,
        },
        {
            id: 'gross',
            header: 'Gross',
            cell: ({ row }) => <span data-testid="line-gross">{row.original.gross}</span>,
        },
    ];

    return (
        <div>
            {title && <h3 className="mb-2 text-sm font-semibold text-mp-foreground">{title}</h3>}
            <Table<T>
                data={lines}
                columns={columns}
                enableSorting={false}
                emptyMessage={emptyMessage}
                getRowId={getLineId}
            />
        </div>
    );
}
